import { useMemo, useRef } from "react";

import { Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { useLayoutStore } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import {
  RestockingItemStatus,
  TranslatedRestockingItem,
} from "flows/Inventory/flows/RestockingList/models/restockingItem/types";
import useSwipeScroll from "shared/hooks/useSwipeScroll";
import { TrashIcon, PenIcon } from "ui/Icons/Icons";
import { PrivateRestockingListItemCard } from "ui/PrivateRestockingListItemCard/PrivateRestockingListItemCard";
import { SideButton } from "ui/SideButton/SideButton";
import { SwipeScrollWrapper } from "ui/SwipeScrollWrapper";

import { RestockingListCard } from "./RestockingListCard";
import { useRestockingServiceStore } from "../stores/restockingServiceStore";

export function NewRestockingListItem({
  item,
  scrollContainer,
  onRemove,
  elementRef,
}: {
  item: TranslatedRestockingItem;
  elementRef: React.RefObject<HTMLDivElement | null>;
  scrollContainer: React.RefObject<HTMLDivElement | null>;
  onRemove: (sku: string) => Promise<void>;
}) {
  const intl = useIntl();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const { handleSwipeScroll } = useSwipeScroll(scrollContainer ?? { current: null }, wrapperRef);
  const { setUseRestockingServiceStore } = useRestockingServiceStore(
    useShallow((state) => ({
      setUseRestockingServiceStore: state.setUseRestockingServiceStore,
    })),
  );
  const { pageName } = useLayoutStore(
    useShallow((state) => {
      return {
        pageName: state.pageName,
      };
    }),
  );

  const onAdjustStock = () => {
    setUseRestockingServiceStore({
      isNavigatingToStockCorrection: true,
      productSku: item.sku,
      activeItemId: item.sku,
      pageName,
    });
    navigate(routes.inventory.stockCorrections.declare);
  };
  const isRestocked = item.status === RestockingItemStatus.done;

  const mainComponent = (
    <Box ref={elementRef} w="full">
      <RestockingListCard
        isRestocked={isRestocked}
        key={item.sku}
        name={item?.product?.name ?? ""}
        imageUrl={item?.product?.imageUrl ?? null}
        stockOnShelf={item?.product?.stockOnShelf ?? 0}
        shelf={item?.product?.shelf ?? null}
        numberOfShelfFacings={item?.product?.numberOfShelfFacings ?? null}
        numberOfShelfStackings={item?.product?.numberOfShelfStackings ?? null}
        isShelvedInHandlingUnits={item?.product?.isShelvedInHandlingUnits ?? null}
        priorityTier={item?.priorityTier ?? null}
      />
    </Box>
  );

  const deleteAction = (
    <Box flex="0 0 137px">
      <SideButton
        height="50%"
        color="red.500"
        bgColor="red.100"
        icon={<TrashIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "flows.inventory.restocking-list.pages.list-preparation.delete-label",
          defaultMessage: "Delete",
        })}
        onClick={() => onRemove(item.sku)}
        data-testid="delete-product"
      />
      <SideButton
        height="50%"
        color="purple.500"
        bgColor="purple.100"
        icon={<PenIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "flows.inventory.restocking-list.pages.list-preparation.adjust-stock-label",
          defaultMessage: "Adjust stock",
        })}
        onClick={() => onAdjustStock()}
        data-testid="adjust-stock-product"
      />
    </Box>
  );

  return (
    <SwipeScrollWrapper
      mainComponent={mainComponent}
      handleSwipeScroll={handleSwipeScroll}
      actionsRight={deleteAction}
      showActionsRight={!isRestocked}
      ref={wrapperRef}
    />
  );
}

export function RestockingListItem({
  restockingItem,
  isClickable = false,
  isShowingCompleteCardsAsBlocked = false,
  isShowingSwipeButtons = false,
  isLoading = false,
  scrollContainer = null,
  elementRef = { current: null },
  onClick = () => {},
  onClickRemove = () => {},
  isDisabled,
}: {
  restockingItem: TranslatedRestockingItem;
  isClickable?: boolean;
  isShowingCompleteCardsAsBlocked?: boolean;
  isShowingSwipeButtons?: boolean;
  isLoading?: boolean;
  scrollContainer?: React.RefObject<HTMLDivElement | null> | null;
  elementRef?: React.RefObject<HTMLDivElement | null>;
  onClick?: () => void;
  onClickRemove?: (sku: string) => void;
  isDisabled?: boolean;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { handleSwipeScroll } = useSwipeScroll(scrollContainer ?? { current: null }, wrapperRef);

  const translatedLabels = useMemo(
    () => ({
      bioLabel: intl.formatMessage({
        id: "flows.inventory.restocking-list.bio-label",
      }),
      nonBioLabel: intl.formatMessage({
        id: "flows.inventory.restocking-list.non-bio-label",
      }),
      inStockLabel: intl.formatMessage({
        id: "flows.inventory.restocking-list.in-stock-label",
      }),
      backOnShelfLabel: intl.formatMessage({
        id: "flows.inventory.restocking-list.back-on-shelf-label",
      }),
    }),
    [intl],
  );

  const isRestockingComplete = restockingItem.status === RestockingItemStatus.done;
  const isBlocked = isShowingCompleteCardsAsBlocked && isRestockingComplete;

  const navigateToStockCorrectionDeclaration = () => {
    navigate(routes.inventory.stockCorrections.declare);
  };

  const mainComponent = (
    <PrivateRestockingListItemCard
      key={restockingItem.sku}
      restockingItem={restockingItem}
      onClickCard={onClick}
      isRestockingComplete={isRestockingComplete}
      isBlocked={isBlocked}
      isClickable={isClickable}
      isLoading={isLoading}
      onAdjustStockClick={navigateToStockCorrectionDeclaration}
      {...translatedLabels}
      ref={elementRef}
    />
  );

  const deleteAction = (
    <Box flex="0 0 137px">
      <SideButton
        height="100%"
        color="red.500"
        bgColor="red.100"
        icon={<TrashIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "flows.inventory.restocking-list.pages.list-preparation.delete-label",
          defaultMessage: "Delete",
        })}
        onClick={() => onClickRemove(restockingItem.sku)}
        disabled={isDisabled}
        data-testid="delete-product"
      />
    </Box>
  );

  return (
    <SwipeScrollWrapper
      mainComponent={mainComponent}
      handleSwipeScroll={handleSwipeScroll}
      actionsRight={deleteAction}
      showActionsRight={isShowingSwipeButtons}
      ref={wrapperRef}
    />
  );
}
